import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { FaBed, FaBath, FaRulerCombined, FaWarehouse, FaBuilding } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { PropertyContext } from '../context/PropertyContext'; // Make sure this import is correct

export default function PropertyDetail() {
  const { id } = useParams();
  const { properties } = useContext(PropertyContext);

  if (!Array.isArray(properties)) {
    console.warn('Properties is not an array:', properties);
    return <p>Loading properties...</p>;
  }

  const property = properties.find(p => p?.id?.toString() === id?.toString());

  if (!property) {
    console.warn('No matching property found for id:', id);
    return <p>Property not found for ID: {id}</p>;
  }

  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (property?.images?.length > 0) {
      setSelectedImage(property.images[0]);
      setCurrentIndex(0);
    } else {
      setSelectedImage(null);
      setCurrentIndex(0);
    }
    window.scrollTo(0, 0);
  }, [property]);

  const nextImage = () => {
    if (!property.images) return;
    const nextIdx = (currentIndex + 1) % property.images.length;
    setCurrentIndex(nextIdx);
    setSelectedImage(property.images[nextIdx]);
  };

  const prevImage = () => {
    if (!property.images) return;
    const prevIdx = (currentIndex - 1 + property.images.length) % property.images.length;
    setCurrentIndex(prevIdx);
    setSelectedImage(property.images[prevIdx]);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative md:h-[70vh] h-[40vh] overflow-hidden bg-slate-800">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 3, ease: "easeOut" }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/images/map-background.jpg')" }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-30" />
      </div>

      <div className="mx-8 md:mx-24">
        {/* Main Image */}
        {selectedImage && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative z-20 -mt-[8%] md:-mt-[15%] px-4 md:px-20 "
          >
            <img
              src={selectedImage}
              alt="Selected"
              onClick={() => setIsFullscreen(true)}
              className="w-full max-h-[70vh] md:max-h-[500px] object-contain shadow-xl rounded-lg cursor-pointer"
            />
          </motion.div>
        )}

       {/* Fullscreen Modal */}
{isFullscreen && (
  <div
    className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50"
    onClick={() => setIsFullscreen(false)}
  >
    <motion.img
      src={selectedImage}
      alt="Fullscreen"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="
        object-contain rounded-lg
        max-h-[90vh] max-w-[95vw]   /* default (mobile) */
        md:max-h-[95vh] md:max-w-[90vw]  /* bigger on medium screens */
        lg:max-h-[95vh] lg:max-w-[85vw]  /* even bigger on large screens */
      "
    />
    <button
      className="absolute top-4 right-6 text-white text-3xl font-bold"
      onClick={(e) => {
        e.stopPropagation();
        setIsFullscreen(false);
      }}
    >
      ✕
    </button>
  </div>
)}

        {/* Image Slider */}
        <div className="mt-6 px-4 md:px-20 relative">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide py-2">
            {property.images?.map((img, idx) => (
              <img
                key={idx}
                src={`${img}`}
                alt={`Thumbnail ${idx}`}
                onClick={() => {
                  setSelectedImage(img);
                  setCurrentIndex(idx);
                }}
                className={`cursor-pointer h-20 w-28 object-cover border-2 transition-all duration-300 ${
                  selectedImage === img ? 'border-blue-500 scale-105' : 'border-transparent'
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between items-center mt-2">
            <button
              onClick={prevImage}
              className="bg-slate-800 text-white px-4 py-1 shadow hover:bg-blue-600"
            >
              Prev
            </button>
            <button
              onClick={nextImage}
              className="bg-slate-800 text-white px-4 py-1 shadow hover:bg-blue-600"
            >
              Next
            </button>
          </div>
        </div>

        {/* Property Info */}
        <section className="flex flex-col md:flex-row items-start md:items-center justify-between px-6 py-12 bg-white md:mx-16 mx-2">
          <div className="md:w-1/2 mb-6 md:mb-0">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              {property.title || 'Big Title Here'}
            </h1>
          </div>

          <div className="md:w-1/2 flex flex-col md:flex-row gap-4">
            <div className="bg-teal-500 text-white px-3 py-2 shadow">
              <p className="text-lg font-semibold">{property.status.toUpperCase() || 'SOLD!'}</p>
            </div>
            <div className="bg-black text-white px-3 py-2 shadow">
              <p className="text-lg font-semibold">${property.price}</p>
            </div>
          </div>
        </section>

        <hr className="my-2 border-slate-300 mx-20" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-10 px-4 md:px-20"
        >
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-gray-800 text-sm md:text-lg mb-6">
            <span className="flex items-center">
              <FaBed className="mr-1 text-teal-500" /> {property.bedrooms} Bedrooms
            </span>
            <span className="flex items-center">
              <FaBath className="mr-1 text-teal-500" /> {property.bathrooms} Bathrooms
            </span>
            <span className="flex items-center">
              <FaRulerCombined className="mr-1 text-teal-500" /> {property.area} sq ft
            </span>
            <span className="flex items-center">
              <FaWarehouse className="mr-1 text-teal-500" /> {property.garages} Garages
            </span>
            <span className="flex items-center">
              <FaBuilding className="mr-1 text-teal-500" /> {property.yearBuilt} Years Build
            </span>
          </div>
          <div className="bg-white p-6">
            {/* Optional description */}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
